import { Injectable } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import {
  ApiConnectionActivityLog,
  ChannelImportStatus,
  Connection,
  ImportedConnectionActivityLog,
  isTerminalImportStatus,
} from '@tv3/store/connection/connection.model'
import { EMPTY, forkJoin, Observable, of, timer } from 'rxjs'
import { ChannelNameTokens, deserializeArray, isSomething } from '@tokeet-frontend/tv3-platform'
import { catchError, filter, map, reduce, switchMap, takeUntil, takeWhile, tap } from 'rxjs/operators'
import * as R from 'ramda'
import {
  AddExportedCalendarConnectionPayload,
  AddImportedCalendarConnectionPayload,
  ApiConnectionStatus,
  ChannelRoomRateResponse,
  CreateChannelListingPayload,
  GetChannelRoomRatesPayload,
  LinkConnectionWithRentalPayload,
  PushRatesToApiConnectionPayload,
  RefreshImportedCalendarConnectionPayload,
  UnlinkConnectionWithRentalPayload,
} from '@tv3/store/connection/connection.types'
import * as lodash from 'lodash'
import {
  HoliduChannelService,
  HVMIChannelService,
  RentalsUnitedChannelService,
  TiketChannelService,
  GVRChannelService,
  CtripChannelService,
  ChannelListingError,
} from '@tokeet-frontend/channels'
import { PushRatesToCM2ApiConnectionPayload } from './connection.types'

export interface ChannelImportStatusResponse {
  channelId: string
  propertyId: string | number
  import_status?: ChannelImportStatus
  import_error?: string
  import_error_time?: number
}

export interface ImportBookingsResponse {
  bookings?: number
  queued?: boolean | number
  job_id?: string
}

function isHomeToGoConnection(c: any): boolean {
  const matchesHomeToGo = (value: unknown) => typeof value === 'string' && value.toLowerCase() === 'hometogo'
  return (
    matchesHomeToGo(c?.source) || matchesHomeToGo(c?.name) || matchesHomeToGo(c?.agent) || matchesHomeToGo(c?.channel)
  )
}

@Injectable({
  providedIn: 'root',
})
export class ConnectionService {
  constructor(
    private http: HttpClient,
    private gvrChannelService: GVRChannelService,
    private holidu: HoliduChannelService,
    private hvmi: HVMIChannelService,
    private rentalsUnited: RentalsUnitedChannelService,
    private tiketService: TiketChannelService,
    private ctripService: CtripChannelService
  ) {}

  static getBackendURLIdentityForChannel(channelName: ChannelNameTokens, includeNew = false) {
    let url = ''
    switch (channelName) {
      case ChannelNameTokens.ExpediaAPI:
        url = 'expedia'
        break
      case ChannelNameTokens.BookingAPI:
        url = 'bdc'
        break
      case ChannelNameTokens.AirBnBV2API:
        url = 'abbv2'
        break
      case ChannelNameTokens.AirBnBAPI:
        url = 'abb'
        break
      case ChannelNameTokens.AgodaAPI:
        url = 'agoda'
        break
      case ChannelNameTokens.Homeaway:
        url = 'homeaway'
        break
    }

    if (includeNew) {
      switch (channelName) {
        case ChannelNameTokens.Holidu:
          url = 'holidu'
          break
        case ChannelNameTokens.Hvmi:
          url = 'hvmi'
          break
        case ChannelNameTokens.RentalsUnited:
          url = 'rentalsunited'
          break
        case ChannelNameTokens.Tiket:
          url = 'tiket'
          break
        case ChannelNameTokens.Trip:
          url = 'ctrip'
          break
        case ChannelNameTokens.GoogleVacationRentals:
          url = 'gvr'
          break
        case ChannelNameTokens.Hopper:
          url = 'hopper'
          break
      }
    }

    return url
  }

  getCM2Connections() {
    return this.http.get<{ listings: object[]; errors: any[] }>(`@api/channel/cm2/listings`).pipe(
      map((res) => res.listings),
      map((listings) => R.reject(isHomeToGoConnection, listings)),
      deserializeArray<Connection>(Connection)
    )
  }

  getCM1Connections() {
    return this.http.get<object[]>('@api/channel/all/').pipe(
      map((channels) =>
        R.reject(
          (c: any) =>
            R.isNil(c.channel_id) &&
            R.isNil(c.propertyName) &&
            R.isNil(c.propertyId) &&
            R.isNil(c.rental_id) &&
            R.isNil(c.linkDate) &&
            R.isNil(c.name),
          channels
        )
      ),
      map((channels) => R.reject(isHomeToGoConnection, channels)),
      map((response) =>
        R.map((r: any) => {
          r.legacyId = r.id
          delete r.id
          return r
        }, response)
      ),
      deserializeArray<Connection>(Connection)
    )
  }

  all(): Observable<Connection[]> {
    return forkJoin([this.getCM1Connections(), this.getCM2Connections()]).pipe(map((data) => R.flatten(data)))
  }

  get(channelName: ChannelNameTokens, roomId: any, propertyId: any) {
    let observer = of(null)
    if (channelName === ChannelNameTokens.RentalsUnited) {
      observer = this.rentalsUnited.getConnection(propertyId)
    }

    return observer.pipe(
      filter(isSomething),
      map((data) => Connection.deserialize(data))
    )
  }

  allByChannelWithErrors(
    channelName: ChannelNameTokens,
    channelId: string
  ): Observable<{ connections: Connection[]; errors: ChannelListingError[] }> {
    if (channelName === ChannelNameTokens.Trip) {
      return this.ctripService.getConnections(true).pipe(
        map(({ listings, errors }) => ({
          connections: listings.map((listing) => Connection.deserialize(listing)),
          errors: errors || [],
        }))
      )
    }

    return this.allByChannel(channelName, channelId).pipe(map((connections) => ({ connections, errors: [] })))
  }

  allByChannel(channelName: ChannelNameTokens, channelId: string): Observable<Connection[]> {
    if (channelName === ChannelNameTokens.Holidu) {
      return this.holidu.getConnections().pipe(deserializeArray<Connection>(Connection))
    } else if (channelName === ChannelNameTokens.Hvmi) {
      return this.hvmi.getConnections().pipe(deserializeArray<Connection>(Connection))
    } else if (channelName === ChannelNameTokens.RentalsUnited) {
      return this.rentalsUnited.getConnections().pipe(deserializeArray<Connection>(Connection))
    } else if (channelName === ChannelNameTokens.GoogleVacationRentals) {
      return this.gvrChannelService.getConnections().pipe(deserializeArray<Connection>(Connection))
    } else if (channelName === ChannelNameTokens.Hopper) {
      return this.getCM2Connections()
    }

    let url = ''
    const channelPath = ConnectionService.getBackendURLIdentityForChannel(channelName)

    if (channelName === ChannelNameTokens.Tiket) {
      return this.tiketService.getConnections().pipe(deserializeArray<Connection>(Connection))
    } else if (channelName === ChannelNameTokens.Trip) {
      return this.ctripService.getConnections().pipe(deserializeArray<Connection>(Connection))
    } else if (channelPath) {
      url = `@api/channel/all?channel_id=${channelId}`
      return this.http.get(url).pipe(deserializeArray<Connection>(Connection))
    }

    return of([])
  }

  allByRental(rentalId: string): Observable<Connection[]> {
    const url = `@api/channel/rental/api/${rentalId}`

    return this.http.get(url).pipe(deserializeArray<Connection>(Connection))
  }

  refreshForChannel(channelName: ChannelNameTokens, channelId: string) {
    if (channelName === ChannelNameTokens.Tiket) {
      return this.tiketService.getConnections()
    } else if (channelName === ChannelNameTokens.Trip) {
      return this.ctripService.getConnections()
    }

    const channelPath = ConnectionService.getBackendURLIdentityForChannel(channelName)
    if (!channelPath) {
      return of([])
    }

    const url = `@api/${channelPath}/refresh/rooms/${channelId}`
    return this.http.get(url)
  }

  getExportedCalendarsByChannelName(channelName: string): Observable<Connection[]> {
    const url = `@api/channel/exported/all/`
    return this.http.get<object[]>(url).pipe(
      map((res) =>
        R.filter((item: any) => {
          const name = (item.name + '').toLowerCase()
          return name.indexOf((channelName + '').toLowerCase()) > -1
        }, res)
      ),
      deserializeArray<Connection>(Connection)
    )
  }

  addImportedCalendarConnection(payload: AddImportedCalendarConnectionPayload): Observable<Connection> {
    const url = `@api/calendar/import`

    return this.http.put(url, payload).pipe(map((res) => Connection.deserialize(res)))
  }

  addExportedCalendarConnection(payload: AddExportedCalendarConnectionPayload): Observable<Connection> {
    const url = `@api/channel/`

    return this.http.post(url, payload).pipe(map((res) => Connection.deserialize(res)))
  }

  createListing(channelName: ChannelNameTokens, payload: CreateChannelListingPayload) {
    const url = `@api/${ConnectionService.getBackendURLIdentityForChannel(channelName)}/publish/create/`
    return this.http.post<{ id: string; name: string; warnings?: string[] }>(url, payload)
  }

  link(payload: LinkConnectionWithRentalPayload) {
    const url = '@api/channel/rental/link'
    return this.http.put(url, payload)
  }

  linkABBV1(payload: LinkConnectionWithRentalPayload) {
    const url = '@api/abb/rental/link'
    return this.http.put(url, payload)
  }

  linkABBV2(payload: LinkConnectionWithRentalPayload) {
    const url = '@api/abbv2/rental/link'
    return this.http.put(url, payload)
  }

  unlink(payload: UnlinkConnectionWithRentalPayload) {
    const url = '@api/channel/rental/unlink'
    return this.http.put(url, payload)
  }

  unlinkABB(payload: UnlinkConnectionWithRentalPayload) {
    const url = '@api/abb/rental/unlink'
    return this.http.put(url, payload)
  }

  unlinkABBV2(payload: UnlinkConnectionWithRentalPayload) {
    const url = '@api/abbv2/rental/unlink'
    return this.http.put(url, payload)
  }

  importBookingByInquiryIdABBV2(channelId: string, connection: Connection, inquiryId: string) {
    const url = `@api/${ConnectionService.getBackendURLIdentityForChannel(
      ChannelNameTokens.AirBnBV2API
    )}/import/old/bookings/${channelId}`
    connection = lodash.merge(new Connection(), connection) // ensure we can call serialize
    return this.http.post(url, { ...connection.serialize(), conf_code: inquiryId })
  }

  importBookings(
    channelName: ChannelNameTokens,
    channelId: string,
    connection: Connection
  ): Observable<ImportBookingsResponse> {
    let url = ''
    switch (channelName) {
      case ChannelNameTokens.ExpediaAPI:
        url = `@api/expedia/import/bookings/${channelId}`
        break
      default:
        url = `@api/${ConnectionService.getBackendURLIdentityForChannel(channelName)}/import/old/bookings/${channelId}`
        break
    }
    connection = lodash.merge(new Connection(), connection) // ensure we can call serialize
    return this.http.post<ImportBookingsResponse>(url, connection.serialize())
  }

  // Lifecycle of the most recent bookings-import job recorded for the connection
  // (TV4-5905), written by DBObjects::CMAccountOAuth::updateChannelImportStatus.
  getImportStatus(channelName: ChannelNameTokens, channelId: string): Observable<ChannelImportStatusResponse> {
    const url = `@api/${ConnectionService.getBackendURLIdentityForChannel(channelName)}/import/status/${channelId}`
    return this.http.get<ChannelImportStatusResponse>(url)
  }

  // Emits only the queued job's recorded status until it turns terminal (done/failed),
  // no matching job is on record after a few successful polls, or timeoutMs passes.
  // Transport failures emit nothing and the next timer tick retries, so they neither
  // erase the last displayed state nor count as evidence that the job is absent.
  pollImportStatus(
    channelName: ChannelNameTokens,
    channelId: string,
    jobId: string,
    intervalMs = 3000,
    timeoutMs = 10 * 60 * 1000
  ): Observable<ChannelImportStatus | null> {
    let misses = 0
    return timer(0, intervalMs).pipe(
      switchMap(() => this.getImportStatus(channelName, channelId).pipe(catchError(() => EMPTY))),
      map((res) => res?.import_status || null),
      tap((status) => (misses = status?.job_id === jobId ? 0 : misses + 1)),
      takeUntil(timer(timeoutMs)),
      takeWhile((status) => !(status?.job_id === jobId && isTerminalImportStatus(status)) && misses < 3, true),
      filter((status) => !status || status.job_id === jobId)
    )
  }

  // Resolves once with the job's terminal status, or with null when the outcome is
  // unknowable: no job on record, or the job still running when timeoutMs passes.
  waitForImportResult(
    channelName: ChannelNameTokens,
    channelId: string,
    jobId: string,
    intervalMs = 3000,
    timeoutMs = 10 * 60 * 1000
  ) {
    return this.pollImportStatus(channelName, channelId, jobId, intervalMs, timeoutMs).pipe(
      reduce((_, status) => status, null as ChannelImportStatus | null),
      map((status) => (isTerminalImportStatus(status) ? status : null))
    )
  }

  // Pulls the channel's booking event feed (new / modified / cancelled bookings),
  // unlike importBookings above which only reconciles the active-reservations
  // summary and therefore never applies modifications or cancellations.
  importNewBookings(channelName: ChannelNameTokens, channelId: string, connection: Connection) {
    const url = `@api/${ConnectionService.getBackendURLIdentityForChannel(channelName)}/import/bookings/${channelId}`
    connection = lodash.merge(new Connection(), connection) // ensure we can call serialize
    return this.http.post(url, connection.serialize())
  }

  pushAvailabilityCM2(channelId: string, rentalId: string) {
    const url = `@api/channel/cm2/push/availability/`
    return this.http.post(url, { channel_id: channelId, rental_id: rentalId })
  }

  pushAvailabilityCM1(channelName: ChannelNameTokens, channelId: string, connection: Connection) {
    const url = `@api/${ConnectionService.getBackendURLIdentityForChannel(channelName)}/push/availability/${channelId}`
    connection = lodash.merge(new Connection(), connection) // ensure we can call serialize
    return this.http.post(url, connection.serialize())
  }

  pushAvailability(channelName: ChannelNameTokens, channelId: string, connection: Connection) {
    const cm2Channels = [
      ChannelNameTokens.GoogleVacationRentals,
      ChannelNameTokens.Tiket,
      ChannelNameTokens.Trip,
      ChannelNameTokens.Holidu,
      ChannelNameTokens.Hvmi,
      ChannelNameTokens.RentalsUnited,
      ChannelNameTokens.Hopper,
    ]
    if (cm2Channels.includes(channelName)) {
      return this.pushAvailabilityCM2(channelId, connection.rentalId)
    } else {
      return this.pushAvailabilityCM1(channelName, channelId, connection)
    }
  }

  pushRatesCM2(payload: PushRatesToCM2ApiConnectionPayload) {
    const url = `@api/channel/cm2/push/rates/`
    return this.http.post(url, payload)
  }

  pushRatesCM1(channelName: ChannelNameTokens, channelId: string, payload: PushRatesToApiConnectionPayload) {
    const url = `@api/${ConnectionService.getBackendURLIdentityForChannel(channelName)}/push/rates/${channelId}`
    return this.http.post(url, payload)
  }

  pushRates(channelName: ChannelNameTokens, channelId: string, payload: PushRatesToApiConnectionPayload) {
    const cm2Channels = [
      ChannelNameTokens.GoogleVacationRentals,
      ChannelNameTokens.Tiket,
      ChannelNameTokens.Trip,
      ChannelNameTokens.Holidu,
      ChannelNameTokens.Hvmi,
      ChannelNameTokens.RentalsUnited,
      ChannelNameTokens.Hopper,
    ]
    if (cm2Channels.includes(channelName)) {
      return this.pushRatesCM2({
        rental_id: payload.rental_id,
        channel_id: channelId,
        rate_plan_id: payload.rateid,
        category: payload.category,
      })
    } else {
      return this.pushRatesCM1(channelName, channelId, payload)
    }
  }

  getRoomRatesFromConnection(
    channelName: ChannelNameTokens,
    channelId: string,
    payload: GetChannelRoomRatesPayload
  ): Observable<ChannelRoomRateResponse[]> {
    if (channelName === ChannelNameTokens.Tiket) {
      return this.tiketService.getRoomRatePlans(payload.propertyId).pipe(
        map((items) =>
          items.map(
            (item) =>
              ({
                id: lodash.toString(item.code),
                name: item.name,
              } as ChannelRoomRateResponse)
          )
        )
      )
    } else if (channelName === ChannelNameTokens.Trip) {
      return this.ctripService.getRoomRatePlans(payload.propertyId).pipe(
        map((items) =>
          items.map(
            (item) =>
              ({
                id: lodash.toString(item.code),
                name: item.name,
              } as ChannelRoomRateResponse)
          )
        )
      )
    } else {
      const url = `@api/${ConnectionService.getBackendURLIdentityForChannel(channelName)}/roomrates/${channelId}`
      return this.http.post<ChannelRoomRateResponse[]>(url, payload)
    }
  }

  getApiConnectionLog(
    channelName: ChannelNameTokens,
    propertyId: string | number,
    roomId?: string | number
  ): Observable<ApiConnectionActivityLog[]> {
    const path = ConnectionService.getBackendURLIdentityForChannel(channelName, true)
    if (!path) {
      return of([])
    }
    const url = `@api/${path}/activity/log/${propertyId}?${!!roomId ? `roomid=${roomId}` : ''}`
    return this.http.get(url).pipe(deserializeArray<ApiConnectionActivityLog>(ApiConnectionActivityLog))
  }

  getApiConnectionStatus(
    channelName: ChannelNameTokens,
    propertyId: string | number,
    roomId: string | number
  ): Observable<ApiConnectionStatus> {
    const path = ConnectionService.getBackendURLIdentityForChannel(channelName)
    if (!path) {
      return of({})
    }
    const url = `@api/${path}/update/log/${propertyId}?roomid=${roomId}`
    return this.http.get<ApiConnectionStatus>(url)
  }

  // This method deletes a connected iCal rental from the AdvanceCM API, not the Channel Manager.
  deleteExportedCalendar(id: string) {
    const url = `@api/channel/delete/${id}`
    return this.http.delete(url)
  }

  getImportedConnectionLog(connectionId: string): Observable<ImportedConnectionActivityLog[]> {
    const url = `@api/calendar/import/logs/${connectionId}`
    return this.http.get(url).pipe(deserializeArray<ImportedConnectionActivityLog>(ImportedConnectionActivityLog))
  }

  refreshImportedCalendar(payload: RefreshImportedCalendarConnectionPayload): Observable<Connection> {
    const url = `@api/calendar/import`
    return this.http.put(url, payload).pipe(map((res) => Connection.deserialize(res)))
  }

  deleteImportedCalendar(calendarId: string, rentalId: string, removeAllEvents: boolean) {
    const url = `@api/calendar/import/delete/${rentalId}/${calendarId}?all=${removeAllEvents ? 1 : ''}`
    return this.http.delete(url)
  }

  getConnectionsStatuses(propertyIds: (string | number)[], roomIds: (string | number)[]) {
    const url = '@api/channel/connection/status/'
    return this.http.post(url, {
      properties: propertyIds,
      rooms: roomIds,
    })
  }
}
